import { useEffect, useState } from 'react';
import { AlertTriangle, Check, Lock, Pencil, Phone, Mail, UserCircle } from 'lucide-react';
import { Badge, Button, LoadingState, ProfileAvatar } from '../components/ui';
import { loadMyCustomer, saveMyCustomer, submitAskQuery } from '../lib/account';
import type { CustomerRow } from '../lib/account';
import { currentUser } from '../lib/parse';
import type { ParseRecord } from '../lib/parse';
import { profileInitials } from '../lib/ayurguideProfile';

export function StudentProfile() {
  const [customer, setCustomer] = useState<CustomerRow>();
  const [checked, setChecked] = useState(false);
  useEffect(() => {
    let active = true;
    void loadMyCustomer().then((row) => { if (active) { setCustomer(row); setChecked(true); } }).catch(() => { if (active) setChecked(true); });
    return () => { active = false; };
  }, []);

  return <div className="utility-page">
    <section className="utility-hero"><div className="utility-icon"><UserCircle size={28} /></div><div><h1>Profile</h1><p>Your AyurGuide learner account details</p></div></section>
    {!checked ? <LoadingState /> : customer
      ? <>
        <ProfileViewCard customer={customer} onSaved={(next) => setCustomer({ ...customer, ...next })} />
        <DeleteAccountSection />
      </>
      : <section className="composer-card"><strong>No learner profile linked</strong><small>This sign-in has no AyurGuide customer record on the server, so there is nothing to edit yet.</small></section>}
  </div>;
}

function ProfileViewCard({ customer, onSaved }: { customer: CustomerRow; onSaved: (fields: Partial<CustomerRow>) => void }) {
  const user = currentUser() as ParseRecord | null;
  const name = [customer.First_name, customer.Last_name].filter(Boolean).join(' ') || String(user?.username ?? 'Learner');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ First_name: customer.First_name ?? '', Last_name: customer.Last_name ?? '', Email_id: customer.Email_id ?? '' });

  const save = async () => {
    if (!form.First_name.trim()) { setError('Name is required.'); return; }
    setSaving(true); setError('');
    try {
      await saveMyCustomer(customer.objectId, {
        First_name: form.First_name.trim(),
        Last_name: form.Last_name.trim(),
        Email_id: form.Email_id.trim(),
      });
      onSaved({
        First_name: form.First_name.trim(),
        Last_name: form.Last_name.trim(),
        Email_id: form.Email_id.trim(),
      });
      setEditing(false);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Could not save your profile.');
    } finally { setSaving(false); }
  };

  if (editing) return <div className="composer-card">
    <label>First name<input value={form.First_name} onChange={(event) => setForm({ ...form, First_name: event.target.value })} placeholder="Your name" /></label>
    <label>Last name<input value={form.Last_name} onChange={(event) => setForm({ ...form, Last_name: event.target.value })} placeholder="Surname" /></label>
    <label>Email<input value={form.Email_id} onChange={(event) => setForm({ ...form, Email_id: event.target.value })} placeholder="you@example.com" type="email" /></label>
    <label>Mobile number<div className="locked-field"><Lock size={13} /> {customer.Mob_no || '—'}<span>Linked at signup</span></div></label>
    {error && <small className="field-error">{error}</small>}
    <div className="form-row">
      <Button variant="ghost" onClick={() => { setEditing(false); setError(''); }}>Cancel</Button>
      <Button icon={Check} disabled={saving} onClick={() => void save()}>{saving ? 'Saving…' : 'Save changes'}</Button>
    </div>
  </div>;

  return <div className="profile-card">
    <ProfileAvatar initials={profileInitials(name)} className="profile-large" />
    <div>
      <h2>{name}</h2>
      {customer.Email_id && <p><Mail size={12} /> {customer.Email_id}</p>}
      {customer.Mob_no && <p><Phone size={12} /> {customer.Mob_no}</p>}
      <Badge tone="teal">AyurGuide learner</Badge>
    </div>
    <Button variant="soft" icon={Pencil} onClick={() => setEditing(true)}>Edit</Button>
  </div>;
}

function DeleteAccountSection() {
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [reason, setReason] = useState('');
  const [state, setState] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [error, setError] = useState('');

  const submit = async () => {
    setState('sending'); setError('');
    try {
      // Legacy DeleteAccount.js parity: a "Delete My Account" query to the team.
      await submitAskQuery('Delete My Account', reason.trim() || 'Please delete my account.');
      setState('sent');
    } catch (submitError) {
      setState('idle');
      setError(submitError instanceof Error ? submitError.message : 'Could not submit the request.');
    }
  };

  return <section className="danger-zone">
    <div className="danger-head"><AlertTriangle size={17} /><strong>Delete account</strong></div>
    {!open ? <>
      <p>This sends a deletion request to the AyurGuide team, exactly like the legacy app. Your data is removed after review — it cannot be undone.</p>
      <Button variant="danger" onClick={() => setOpen(true)}>Request account deletion</Button>
    </> : state === 'sent' ? <p className="delete-sent"><Check size={15} /> Request submitted. The support team will contact you before removing the account.</p>
      : <>
        <label>Reason (optional)<textarea value={reason} onChange={(event) => setReason(event.target.value)} rows={3} placeholder="Tell us why you are leaving" /></label>
        <label>Type DELETE to confirm<input value={confirmText} onChange={(event) => setConfirmText(event.target.value)} placeholder="DELETE" /></label>
        {error && <small className="field-error">{error}</small>}
        <div className="form-row">
          <Button variant="ghost" onClick={() => { setOpen(false); setConfirmText(''); }}>Cancel</Button>
          <Button variant="danger" disabled={confirmText !== 'DELETE' || state === 'sending'} onClick={() => void submit()}>{state === 'sending' ? 'Sending…' : 'Submit request'}</Button>
        </div>
      </>}
  </section>;
}
